import axios from "axios";

export interface PDFDownloadResult {
  path: string;
  base64?: string | null;
  name?: string | null;
  size?: number | null;
  error?: string | null;
}

/**
 * Converter ArrayBuffer para base64 (sem usar Buffer do Node.js)
 * Funciona no navegador
 */
function arrayBufferToBase64(arrayBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';

  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary);
}

/**
 * Versão 1: Usando Fetch (Recomendado para Frontend)
 * Não precisa de axios, usa fetch nativo
 */
async function downloadPdfsWithFetch(paths: string[]): Promise<PDFDownloadResult[]> {
  console.log('Downloading PDFs with fetch...');

  try {
    const promises = paths.map(async (path): Promise<PDFDownloadResult> => {
      try {
        console.log(`Downloading: ${path}`);

        const response = await fetch(path);

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const base64 = arrayBufferToBase64(arrayBuffer);

        const fileName = path.split('/').pop() || 'documento.pdf';

        console.log(`Downloaded: ${fileName}`);

        return {
          path: path,
          base64: base64,
          name: fileName,
          size: blob.size,
        };
      } catch (error: any) {
        console.error(`Error on download ${path}:`, error.message);
        return {
          path: path,
          base64: null,
          error: error.message,
        };
      }
    });

    return await Promise.all(promises);
  } catch (error: any) {
    console.error('Generic error:', error.message);
    throw error;
  }
}

/**
 * Versão 2: Usando Axios (Se você quiser usar axios mesmo)
 * Converte ArrayBuffer para base64 sem Buffer
 */
async function downloadPdfsWithAxios(paths: string[]): Promise<PDFDownloadResult[]> {
  console.log('Downloading PDFs with axios...');

  try {
    const promises = paths.map(async (path): Promise<PDFDownloadResult> => {
      try {
        console.log(`Downloading: ${path}`);

        const response = await axios.get(path, {
          responseType: 'arraybuffer',
          timeout: 30000,
          headers: {
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          },
        });

        // Converter ArrayBuffer para base64 SEM usar Buffer
        const base64 = arrayBufferToBase64(response.data);
        const fileName = path.split('/').pop() || 'documento.pdf';

        console.log(`Downloaded: ${fileName}`);

        return {
          path: path,
          base64: base64,
          name: fileName,
          size: response.data.byteLength,
        };
      } catch (error: any) {
        console.error(`Error on download ${path}:`, error.message);
        return {
          path: path,
          base64: null,
          error: error.message,
        };
      }
    });

    return await Promise.all(promises);
  } catch (error: any) {
    console.error('Generic error:', error.message);
    throw error;
  }
}

/**
 * Versão 3: Com paralelismo controlado
 */
async function downloadPdfsParallel(
  paths: string[],
  paralelismo: number = 3
): Promise<PDFDownloadResult[]> {
  console.log(`Downloading ${paths.length} PDFs with ${paralelismo} parallel requests...`);

  const resultados: PDFDownloadResult[] = [];

  for (let i = 0; i < paths.length; i += paralelismo) {
    const lote = paths.slice(i, i + paralelismo);

    const promises = lote.map(async (path): Promise<PDFDownloadResult> => {
      try {
        const response = await fetch(path);

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const base64 = arrayBufferToBase64(arrayBuffer);
        const fileName = path.split('/').pop() || 'documento.pdf';

        return {
          path: path,
          base64: base64,
          name: fileName,
          size: blob.size,
        };
      } catch (error: any) {
        console.error(`Error: ${path}`, error.message);
        return {
          path: path,
          base64: null,
          error: error.message,
        };
      }
    });

    const resultadosLote = await Promise.all(promises);
    resultados.push(...resultadosLote);
  }

  return resultados;
}

async function fetchPdfs(paths: string[]) {
  console.log('fetching pdfs...');

  const pdfs: PDFDownloadResult[] = [];

  for (const path of paths) {
    try {
      const response = await fetch(path);
      const blob = await response.blob();

      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result;
          if (typeof result === 'string') {
            const base64f = result.split(',')[1];
            resolve(base64f);
          } else {
            reject(new Error('Resultado não é string'));
          }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });

      pdfs.push({
        path: path,
        base64: base64,
        name: path.split('/').pop() || 'documento.pdf',
        size: blob.size,
      });
    } catch (error) {
      console.error(`Erro ao baixar ${path}:`, error);
    }
  }

  return pdfs;
}

export const PdfDownloadHelper = {
  downloadPdfsWithFetch,
  downloadPdfsWithAxios,
  downloadPdfsParallel,
  fetchPdfs,
}